/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import * as React from 'react';

import * as MsDyn365 from '@msdyn365-commerce/core';

import { getTelemetryObject, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';

import { Link } from './components/link';

import { IEcommFooterData } from './ecomm-footer.data';
import { IEcommFooterProps, ILinksData } from './ecomm-footer.props.autogenerated';

export interface IEcommFooterViewProps extends IEcommFooterProps<IEcommFooterData> {
    information: React.ReactNode;
    openingHoursDescription: React.ReactNode;
    etiquetteDescription: React.ReactNode;
    links?: React.ReactNode[];
}

/**
 *
 * EcommFooter component
 * @extends {React.PureComponent<IEcommFooterProps<IEcommFooterData>>}
 */
class EcommFooter extends React.PureComponent<IEcommFooterProps<IEcommFooterData>> {
    private telemetryContent: ITelemetryContent = getTelemetryObject(
        this.props.context.request.telemetryPageName!,
        this.props.friendlyName,
        this.props.telemetry
    );

    public render(): JSX.Element | null {
        const { config } = this.props;

        const navi = this._getCustomNaviLinks(0);

        const information = config.information && (
            <MsDyn365.RichTextComponent
                text={config.information}
                editProps={{
                    onEdit: this._handleOpeningHours,
                    requestContext: this.props.context.request
                }}
            />
        );

        const openingHours = config.openingHoursDescription && (
            <MsDyn365.RichTextComponent
                text={config.openingHoursDescription}
                editProps={{
                    onEdit: this._handleOpeningHours,
                    requestContext: this.props.context.request
                }}
            />
        );

        const etiquette = config.etiquetteDescription && (
            <MsDyn365.RichTextComponent
                text={config.etiquetteDescription}
                editProps={{
                    onEdit: this._handleEtiquette,
                    requestContext: this.props.context.request
                }}
            />
        );

        const EcommFooterViewProps = {
            ...this.props,
            information: information,
            openingHoursDescription: openingHours,
            etiquetteDescription: etiquette,
            links: navi
        };

        return this.props.renderView(EcommFooterViewProps);
    }

    private _getCustomNaviLinks = (itemPosition: number): JSX.Element[] | null => {
        const { links } = this.props.config;

        if (!links || !(links.length > 0)) {
            this.props.context.telemetry.error('Navigation links content is empty, links will not render.');
            return null;
        }

        return links.map((value: ILinksData, index: number) => {
            itemPosition++;

            return (
                <Link
                    key={index}
                    text={value.linkText}
                    context={this.props.context}
                    ariaLabel={value.ariaLabel || value.linkText}
                    href={value.linkUrl && value.linkUrl.destinationUrl}
                    index={{ itemPosition }.toString()}
                    telemetryContent={this.telemetryContent}
                />
            );
        });
    };

    private _handleOpeningHours = (event: MsDyn365.ContentEditableEvent) =>
        (this.props.config.openingHoursDescription = event.target.value);

    private _handleEtiquette = (event: MsDyn365.ContentEditableEvent) => (this.props.config.etiquetteDescription = event.target.value);
}

export default EcommFooter;
